<template>
  <ul class="pagination" v-if="showPagination">
    <li class="pagination--button" v-if="canGoBack">
      <button @click.prevent="previous">&lt;</button>
    </li>

    <li
      v-for="(step, idx) in visibleSteps"
      :key="step.gap ? `gap-${idx}` : step.start"
      :class="{
        'pagination--active':
          !step.gap &&
          currentStep &&
          currentStep.start === step.start
      }"
    >
      <button v-if="step.gap">…</button>
      <button
        v-else
        @click.prevent="changeRange(step.start, step.end)"
      >
        {{ step.start }}-{{ step.end }}
      </button>
    </li>

    <li class="pagination--button" v-if="canGoFwd">
      <button @click.prevent="next">&gt;</button>
    </li>
  </ul>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  total: {
    type: Number,
    required: true,
  },
  step: {
    type: Number,
    default: 100,
  },
  window: {
    type: Number,
    default: 2,
  },
  outerWindow: {
    type: Number,
    default: 1,
  },
  currentStart: {
    type: Number,
    default: 1,
  },
  firstPosition: {
    type: Number,
    default: 1,
  },
});

const emit = defineEmits(["update:range"]);

const allSteps = computed(() => {
  const steps = [];
  const stepSize = props.step;
  const firstStart =
    Math.floor((props.firstPosition - 1) / stepSize) * stepSize;

  for (
    let start = firstStart, end = Math.min(firstStart + stepSize, props.total);
    start < props.total;
    start = end, end = Math.min(end + stepSize, props.total)
  ) {
    steps.push({ start, end });
  }

  return steps;
});

const currentStep = computed(() => {
  if (!allSteps.value.length) return null;

  const found = allSteps.value.find(
    (step) => props.currentStart >= step.start && props.currentStart < step.end
  );

  return found || allSteps.value[0];
});

const visibleSteps = computed(() => {
  if (!allSteps.value.length || !currentStep.value) return [];

  const result = [];
  const currentIdx = allSteps.value.findIndex(
    (step) =>
      step.start === currentStep.value.start &&
      step.end === currentStep.value.end
  );

  for (const [idx, step] of allSteps.value.entries()) {
    if (
      Math.abs(currentIdx - idx) <= props.window ||
      idx < props.outerWindow ||
      idx >= allSteps.value.length - props.outerWindow
    ) {
      result.push(step);
    } else {
      const last = result[result.length - 1];
      if (!last || !last.gap) {
        result.push({ gap: true });
      }
    }
  }

  return result;
});

const canGoBack = computed(() => {
  if (!currentStep.value || !allSteps.value.length) return false;
  const idx = allSteps.value.findIndex(
    (step) =>
      step.start === currentStep.value.start &&
      step.end === currentStep.value.end
  );
  return idx > 0;
});

const canGoFwd = computed(() => {
  if (!currentStep.value || !allSteps.value.length) return false;
  const idx = allSteps.value.findIndex(
    (step) =>
      step.start === currentStep.value.start &&
      step.end === currentStep.value.end
  );
  return idx < allSteps.value.length - 1;
});

const showPagination = computed(() => {
  return props.total > props.step;
});

const changeRange = (from, to) => {
  emit("update:range", { from, to });
};

const previous = () => {
  if (!currentStep.value) return;

  for (let i = 0; i < allSteps.value.length; i++) {
    const step = allSteps.value[i];
    if (
      step.start === currentStep.value.start &&
      step.end === currentStep.value.end &&
      i > 0
    ) {
      const { start, end } = allSteps.value[i - 1];
      changeRange(start, end);
      return;
    }
  }
};

const next = () => {
  if (!currentStep.value) return;

  for (let i = 0; i < allSteps.value.length; i++) {
    const step = allSteps.value[i];
    if (
      step.start === currentStep.value.start &&
      step.end === currentStep.value.end &&
      i < allSteps.value.length - 1
    ) {
      const { start, end } = allSteps.value[i + 1];
      changeRange(start, end);
      return;
    }
  }
};
</script>

<style lang="scss">
.pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.7rem;

  li {
    list-style: none;
  }

  button {
    border: none;
    padding: 0;
    background: transparent;
    font-family: var(--font-important);
    font-weight: 600;
    font-size: 1.2rem;
    color: rgb(var(--color-highlight));
    cursor: pointer;
  }

  li.pagination--active button {
    color: rgb(var(--color-primary));
  }

  li.pagination--button button {
    background-color: rgb(var(--color-highlight));
    color: rgb(var(--color-element-light));
    padding: 8px 14px;
  }
}
</style>
